// src/components/SafetyWatch/SafetyWatchPanel.js
// Main panel: location input → fetches risk data → renders ActivityRiskCards
// Provides full transparency: shows data sources, timestamps, and raw data.

import React, { useState, useCallback } from "react";
import ActivityRiskCard from "./ActivityRiskCard";
import { fetchRiskData, computeAllActivityRisks } from "../../services/riskAssessment";
import { riskLevelMeta, RISK_LEVELS } from "../../constants/activityThresholds";
import { geocodeLocation } from "../../services/geocodingService";

const SafetyWatchPanel = () => {
  // Location state
  const [locationName, setLocationName] = useState("Moab, UT");
  const [lat, setLat] = useState(38.5733);
  const [lon, setLon] = useState(-109.5498);
  const [useManualCoords, setUseManualCoords] = useState(false);

  // Data state
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState(null);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [lastFetched, setLastFetched] = useState(null);
  const [showRawData, setShowRawData] = useState(false);

  // Geocode a location name → lat/lon via Google Maps
  const geocode = async (name) => {
    const coords = await geocodeLocation(name);
    if (!coords) throw new Error("Location not found");
    return { lat: coords.lat, lon: coords.lng };
  };

  const runAssessment = useCallback(async () => {
    setLoading(true);
    setErrors({});
    setResults(null);
    setRawData(null);

    try {
      let useLat = lat;
      let useLon = lon;

      if (!useManualCoords && locationName.trim()) {
        const geo = await geocode(locationName);
        useLat = geo.lat;
        useLon = geo.lon;
        setLat(useLat);
        setLon(useLon);
      }

      const data = await fetchRiskData(useLat, useLon);
      setRawData(data);
      setErrors(data.errors || {});
      const assessed = computeAllActivityRisks(data);
      setResults(assessed);
      setLastFetched(new Date());
    } catch (e) {
      setErrors({ general: e.message });
    } finally {
      setLoading(false);
    }
  }, [lat, lon, locationName, useManualCoords]);

  // Compute the worst overall across all activities
  const worstOverall = results
    ? Math.max(
        ...Object.values(results).map((r) => RISK_LEVELS.indexOf(r.overall))
      )
    : -1;
  const worstLevel = worstOverall >= 0 ? RISK_LEVELS[worstOverall] : null;

  // Count data sources that loaded
  const dataSources = rawData
    ? [
        { name: "Weather.gov Forecast", ok: !!rawData.weather, error: errors.weather },
        { name: "AirNow AQI", ok: Array.isArray(rawData.aqi) && rawData.aqi.length > 0, error: errors.aqi },
        { name: "NWS Active Alerts", ok: Array.isArray(rawData.nwsAlerts), error: errors.nwsAlerts, count: rawData.nwsAlerts?.length || 0 },
      ]
    : [];

  return (
    <div className="max-w-4xl mx-auto">
      {/* ─── Header ─── */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2" style={{ fontFamily: "Times New Roman, Times, serif", letterSpacing: "0.5px", color: "#111111" }}>
          Safety Watch
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Activity-specific risk assessment based on real-time weather, air quality, and NWS alerts.
        </p>
      </div>

      {/* ─── Location input ─── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Location
            </label>
            <input
              type="text"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              placeholder="e.g. Moab, UT or Yosemite National Park"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyDown={(e) => e.key === "Enter" && runAssessment()}
            />
          </div>

          {/* Manual coords toggle */}
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={useManualCoords}
                onChange={(e) => setUseManualCoords(e.target.checked)}
                className="rounded"
              />
              Manual lat/lon
            </label>
          </div>
        </div>

        {useManualCoords && (
          <div className="flex gap-3 mt-2">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Lat</label>
              <input
                type="number"
                step="0.0001"
                value={lat}
                onChange={(e) => setLat(parseFloat(e.target.value))}
                className="w-28 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Lon</label>
              <input
                type="number"
                step="0.0001"
                value={lon}
                onChange={(e) => setLon(parseFloat(e.target.value))}
                className="w-28 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
        )}

        <button
          onClick={runAssessment}
          disabled={loading}
          className="mt-3 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
        >
          {loading ? (
            <>
              <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              Assessing...
            </>
          ) : (
            <>🔍 Run Safety Assessment</>
          )}
        </button>

        {/* Coordinates display */}
        {lastFetched && (
          <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            📍 {lat.toFixed(4)}, {lon.toFixed(4)} · Assessed {lastFetched.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* ─── Error banner ─── */}
      {errors.general && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-700 dark:text-red-300">
          ❌ {errors.general}
        </div>
      )}

      {/* ─── Data Sources Transparency ─── */}
      {dataSources.length > 0 && (
        <div className="mb-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2" style={{ fontFamily: "Times New Roman, Times, serif", letterSpacing: "0.5px" }}>
            Data Sources
            <span className="text-xs font-normal text-gray-400">
              (What feeds into the risk score)
            </span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {dataSources.map((ds) => (
              <div
                key={ds.name}
                className={`flex items-center gap-2 p-2 rounded-lg text-xs ${
                  ds.ok
                    ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
                    : "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400"
                }`}
              >
                <span>{ds.ok ? "✅" : "⚠️"}</span>
                <div>
                  <div className="font-medium">{ds.name}</div>
                  {ds.error && <div className="text-[10px] opacity-70">{ds.error}</div>}
                  {ds.count !== undefined && ds.ok && (
                    <div className="text-[10px] opacity-70">{ds.count} active alert{ds.count !== 1 ? "s" : ""}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Raw data toggle */}
          <button
            onClick={() => setShowRawData((p) => !p)}
            className="mt-2 text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline"
          >
            {showRawData ? "Hide raw API data" : "Show raw API data (developer view)"}
          </button>
          {showRawData && (
            <div className="mt-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 max-h-64 overflow-auto">
              <pre className="text-[10px] text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words">
                {JSON.stringify(
                  {
                    weatherPeriods: rawData?.weather?.properties?.periods?.slice(0, 3) || null,
                    aqi: rawData?.aqi || null,
                    nwsAlerts: rawData?.nwsAlerts?.slice(0, 5)?.map((a) => ({
                      event: a.event,
                      severity: a.severity,
                      headline: a.headline,
                    })) || null,
                  },
                  null,
                  2
                )}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* ─── Overall summary banner ─── */}
      {results && worstLevel && (
        <div
          className="mb-6 p-4 rounded-xl border-2 flex items-center gap-3"
          style={{
            borderColor: riskLevelMeta[worstLevel].border,
            background: riskLevelMeta[worstLevel].bg,
          }}
        >
          <span className="text-3xl">{riskLevelMeta[worstLevel].emoji}</span>
          <div>
            <div className="font-bold text-gray-900 dark:text-white">
              Overall Conditions:{" "}
              <span style={{ color: riskLevelMeta[worstLevel].color }}>
                {riskLevelMeta[worstLevel].label}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Based on the highest risk factor across all activities. Expand each card to see why.
            </div>
          </div>
        </div>
      )}

      {/* ─── Activity cards ─── */}
      {results && (
        <div className="space-y-4">
          {Object.entries(results)
            // Sort worst-risk activities first
            .sort((a, b) => RISK_LEVELS.indexOf(b[1].overall) - RISK_LEVELS.indexOf(a[1].overall))
            .map(([key, assessment]) => (
              <ActivityRiskCard key={key} activityKey={key} assessment={assessment} />
            ))}
        </div>
      )}

      {/* ─── How it works (always visible) ─── */}
      <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: "Times New Roman, Times, serif", letterSpacing: "0.5px" }}>
          ℹ️ How Risk Levels Are Determined
        </h2>
        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-2">
          <p>
            Each activity has multiple <strong>risk factors</strong> (wind, temperature, AQI, weather alerts, etc.)
            evaluated against configurable thresholds:
          </p>
          <div className="flex flex-wrap gap-3 my-2">
            {RISK_LEVELS.map((lvl) => (
              <span key={lvl} className="flex items-center gap-1.5">
                <span
                  className="inline-block w-3 h-3 rounded-full"
                  style={{ background: riskLevelMeta[lvl].color }}
                />
                <span className="font-medium">{riskLevelMeta[lvl].label}</span>
              </span>
            ))}
          </div>
          <p>
            The <strong>overall risk</strong> for an activity equals its <strong>worst individual factor</strong>.
            For example, if hiking has AQI at green but wind at orange, the overall hiking risk is orange.
          </p>
          <p>
            Click <em>"Show how this is calculated"</em> on any activity card to see every factor,
            its current value, and the exact threshold that triggered the level.
          </p>
          <p className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <strong>Data sources:</strong> Weather.gov (NWS) forecast & active alerts, AirNow.gov air quality.
            River conditions use NWS flood alerts as a proxy in this prototype.
            Thresholds can be customized in <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">activityThresholds.js</code>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SafetyWatchPanel;
